import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Leaderboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function shorten(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatUSDC(raw) {
  if (raw == null) return '$0.00';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function Leaderboard() {
  const [rows, setRows]     = useState([]);
  const [period, setPeriod] = useState('all');
  const [game, setGame]     = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ period });
    if (game !== 'all') qs.set('game', game);
    fetch(`${API}/tournament/leaderboard?${qs.toString()}`)
      .then(r => r.json())
      .then(data => { setRows(data.leaderboard || []); setError(null); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [period, game]);

  return (
    <div className="leaderboard">
      <Helmet>
        <title>Leaderboard — Top EZGAME Winners | Apex &amp; Fortnite</title>
        <meta name="description" content="The top-earning players on EZGAME. Weekly, monthly, and all-time leaders for Apex Legends, Fortnite, and CS2 $1 USDC kill tournaments." />
        <link rel="canonical" href="https://www.ezegames.live/leaderboard" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="Leaderboard — Top EZGAME Winners" />
        <meta property="og:description" content="Weekly, monthly, and all-time top earners for Apex, Fortnite, and CS2 kill tournaments. $1 entry, USDC payouts on Polygon." />
        <meta property="og:url"         content="https://www.ezegames.live/leaderboard" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="Leaderboard — Top EZGAME Winners" />
        <meta name="twitter:description" content="Weekly, monthly, and all-time top earners for Apex, Fortnite, and CS2 kill tournaments." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'EZGAME Tournament Leaderboard',
          description: 'Top-earning players on EZGAME across Apex Legends, Fortnite, and CS2 kill tournaments.',
          url: 'https://www.ezegames.live/leaderboard',
        })}</script>
      </Helmet>

      <div className="leaderboard__header">
        <h1 className="leaderboard__title">🏆 Top <span>Earners</span></h1>
        <p className="leaderboard__sub">Total USDC won across EZGAME tournaments. Climb the ranks, get paid.</p>

        <div className="leaderboard__filters">
          <div className="leaderboard__filter-group">
            {['week', 'month', 'all'].map(p => (
              <button
                key={p}
                className={`leaderboard__chip ${period === p ? 'leaderboard__chip--active' : ''}`}
                onClick={() => setPeriod(p)}
              >
                {p === 'all' ? 'All-time' : p === 'week' ? 'This week' : 'This month'}
              </button>
            ))}
          </div>
          <div className="leaderboard__filter-group">
            {[['all', 'All games'], ['apex', 'Apex'], ['fortnite', 'Fortnite'], ['cs2', 'CS2']].map(([k, label]) => (
              <button
                key={k}
                className={`leaderboard__chip ${game === k ? 'leaderboard__chip--active' : ''}`}
                onClick={() => setGame(k)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="leaderboard__status">Loading leaderboard...</div>}
      {error && <div className="leaderboard__error">{error}</div>}

      {!loading && rows.length === 0 && (
        <div className="leaderboard__empty">
          <p>No winners yet for this filter.</p>
          <Link to="/lobby" className="leaderboard__cta">Be the first →</Link>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="leaderboard__table-wrap">
          <table className="leaderboard__table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Wallet</th>
                <th>Wins</th>
                <th>Total Kills</th>
                <th>Best Game</th>
                <th>Total Earned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.rank} className={r.rank <= 3 ? `leaderboard__row--top${r.rank}` : ''}>
                  <td className="leaderboard__rank">
                    {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : r.rank}
                  </td>
                  <td className="leaderboard__handle">{r.handle || '—'}</td>
                  <td className="leaderboard__wallet">{shorten(r.walletAddress)}</td>
                  <td>{r.wins}</td>
                  <td style={{ color: '#00D4FF', fontWeight: 600 }}>
                    {r.totalKills > 0 ? `⚔️ ${r.totalKills}` : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.bestKills != null ? (
                      <span>
                        <span style={{ color: '#00D4FF', fontWeight: 700 }}>{r.bestKills}k</span>
                        {r.bestXp != null && <span style={{ color: 'var(--text-secondary)' }}> / +{r.bestXp.toLocaleString()}xp</span>}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="leaderboard__prize">{formatUSDC(r.totalEarned)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
